import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { ticketActionsQuery, type TicketActionsPage, type TicketFormName } from '../api/easeedesk'
import { Spinner } from '../ui/Spinner'
import { TicketActionForm } from './TicketActionForm'
import { TicketAdvancePanel } from './TicketAdvance'
import { TicketSubordinates } from './TicketSubordinates'
import './TicketActions.css'

/**
 * Fila "Change" - `easeedesk/ticket/actions/index.php`.
 *
 * E prima fila din coloana din dreapta, deci panoul care se deschide singur pe
 * fiecare fisa. Pana acum se deschidea gol.
 *
 * Randul de butoane, exact ca in macheta veche: `btn-group` cu `data-toggle
 * ="buttons"`, si sub el locul in care se incarca ce s-a apasat. "Subordinates"
 * e o citire; celelalte sase deschid formulare de SCRIERE, si toate sase se
 * deseneaza acum din descrierea pe care o da serverul (`TicketActionForm`).
 *
 * PRIMUL BUTON SE APASA SINGUR. `TicketActionTabs()` din
 * `public/js/easeedesk/easeedesk.js` leaga apasarile si incheie cu
 * `.eq(0).trigger('click')` - deci "Advance" e deschis inca de la desenare, si
 * asa arata panoul pe orice fisa din portal: butonul lui plin, celelalte sase
 * cu litere gri (`.btn-theme-inverse.btn-transparent`, adica NEALESE, nu
 * stinse), si sub ele panoul lui.
 *
 * Scria aici, pana la livrarea asta, ca "nimic nu se deschide singur in portal".
 * Nu era asa, si de-aia jumatatea dreapta a fisei se deschidea cu un rand de
 * butoane si nimic sub el, in loc de preluarea tichetului - care e chiar lucrul
 * pe care il vede omul prima data pe un tichet ce nu e al lui.
 */

export function TicketActions({ id }: { id: number }) {
  const { data, error } = useQuery(ticketActionsQuery(id))

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  if (data.buttons.length === 0) {
    /*
     * Nu e acelasi lucru cu "n-are voie": pe un tichet rezolvat, cele cinci
     * butoane de serviciu dispar chiar daca omul are toate drepturile. Portalul
     * lasa aici un rand gol, care semana cu un panou care nu s-a incarcat.
     */
    return (
      <div className="alert alert-info" role="alert">
        <i className="fas fa-info-circle" aria-hidden="true"></i> No change can be made on this ticket.
      </div>
    )
  }

  /* Cheia il face pe React sa reia starea cand tichetul se schimba sub acelasi
     panou - randul desfasurat din lista deschide alt tichet in acelasi loc. */
  return <Buttons key={id} id={id} page={data} />
}

function Buttons({ id, page }: { id: number; page: TicketActionsPage }) {
  /* Primul buton, apasat de la inceput - `.eq(0).trigger('click')`. */
  const [open, setOpen] = useState(() => page.buttons[0]?.id ?? '')

  return (
    <div className="row" style={{ margin: 0 }}>
      <div className={`btn-group tk-actions btn-ticket-actions-${id}`} role="group">
        {page.buttons.map((button) => (
          <label
            key={button.id}
            className={`btn btn-theme-inverse btn-transparent btn-sm${open === button.id ? ' active' : ''}`}
            data-url={`/${button.href}${id}${button.tail}`}
          >
            <input
              id={button.id}
              type="radio"
              name={`types-${id}`}
              autoComplete="off"
              checked={open === button.id}
              onChange={() => setOpen(button.id)}
            />{' '}
            {button.label}
          </label>
        ))}
      </div>

      <div className={`tk-action-content ticket-action-content-${id}`}>
        <Opened id={id} page={page} button={page.buttons.find((b) => b.id === open)} />
      </div>
    </div>
  )
}

function Opened({
  id,
  page,
  button,
}: {
  id: number
  page: TicketActionsPage
  button?: { id: string; href: string; tail: string; label: string }
}) {
  if (!button) return null

  if (button.id === 'status-tab') return <TicketAdvancePanel id={id} advance={page.advance} />
  if (button.id === 'subordinates-tab') return <TicketSubordinates id={id} />

  /* "Waiting for Third Party" n-are buton separat de caz la furnizor: furnizorii
     aplicatiilor de pe tichet sunt chiar in lista formularului, si alegerea
     unuia deschide si cazul (`ThirdPartyEscalation` pe server). */
  const opens = FORM_OF[button.id]
  if (opens) return <TicketActionForm id={id} form={opens[0]} type={opens[1]} />

  /* Portalul vechi nu mai exista pe server, deci nu se mai trimite nimeni
     acolo: se spune ce lipseste inca de pe ecranul asta si atat. */
  return (
    <div className="alert alert-info" role="alert">
      <i className="fas fa-info-circle" aria-hidden="true"></i> {button.label} is not available on this screen yet.
    </div>
  )
}

/**
 * Ce formular deschide fiecare buton, si cu ce fel de escaladare.
 *
 * Cifra e chiar coada adresei vechi (`TicketActions::TAIL` din server): trei
 * butoane deschid ACELASI formular de escaladare si se deosebesc doar prin ea.
 * "Advance" lipseste dinadins - el trece intai prin `TicketAdvancePanel`, care
 * pune in loc alt panou cand tichetul o cere.
 */
const FORM_OF: Record<string, [TicketFormName, number] | undefined> = {
  'update-tab': ['update', 0],
  'planning-tab': ['planning', 0],
  'internal-escalate-tab': ['escalate', 1],
  'waiting-for-customer-tab': ['escalate', 2],
  'waiting-for-third-party-tab': ['escalate', 3],
}
